<script setup lang="ts">
import type { KpiQuerySortDirectionType } from '@carrefour-gcs/shared';
import { useElementBounding } from '@vueuse/core';
import { computed, useTemplateRef, watch } from 'vue';
import VueFeather from 'vue-feather';
import type { DataTableColumnType } from '../dataTable.model';

const props = defineProps<{
  column: DataTableColumnType;
  colSpan: number;
  sortedBy: KpiQuerySortDirectionType | null;
  isFirst: boolean;
  isFixed: boolean;
  fixedLeftPosition: number | null;
}>();

const container = useTemplateRef<HTMLElement>('container');

const { width } = useElementBounding(container);

const emits = defineEmits<{
  sortColumn: [];
  widthChanged: [width: number];
}>();

watch(
  width,
  () => {
    emits('widthChanged', width.value);
  },
  { immediate: true }
);

const containerClasses = computed(() => ({
  'sticky z-20': props.isFixed,
  'border-l-0': !props.isFirst
}));
</script>

<template>
  <th
    ref="container"
    :colspan="colSpan"
    class="text-sm font-bold px-4 py-[18px] text-left text-default border border-b-0 border-solid border-[#EBEBEB] sticky top-0 z-10"
    :class="containerClasses"
    :style="{ left: fixedLeftPosition !== null ? `${fixedLeftPosition}px` : undefined }"
  >
    <div class="flex items-center gap-0.5">
      <p class="w-max max-w-40 whitespace-pre-line">{{ column.label }}<span v-if="column.hasNotif">&nbsp;*</span></p>
      <button v-if="column.isSortable" class="flex flex-col relative w-6 h-6" @click="emits('sortColumn')">
        <template v-if="!sortedBy">
          <VueFeather type="chevron-up" size="24" class="absolute top-[-6px] left-0" aria-hidden="true" />
          <VueFeather type="chevron-down" size="24" class="absolute top-[6px] left-0" aria-hidden="true" />
        </template>
        <VueFeather
          v-else
          :type="sortedBy === 'desc' ? 'chevron-down' : 'chevron-up'"
          size="24"
          class="text-primary"
          aria-hidden="true"
        />
      </button>
    </div>
  </th>
</template>

<style>
.notif:before {
  content: '*';
  position: absolute;
  left: -10px;
  top: 0;
  bottom: 0;
  height: 100%;
  display: grid;
  align-items: center;
}
</style>
